import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import * as showdown from 'showdown';
import { MatDialog } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';

import { FlameThrowerService } from 'src/app/gen2ai/flamethrower.service';
import { Message } from '../../models/chat.model';
import { ModelSelectionDialogComponent } from '../dialogs/model-selection-dialog/model-selection-dialog.component';
import { MaverickComponent } from '../dialogs/maverick/maverick.component';
import { OpusSixComponent } from '../dialogs/opus-six/opus-six.component';
import { HaikuComponent } from '../dialogs/haiku/haiku.component';
import { SonnetFourComponent } from '../dialogs/sonnet-four/sonnet-four.component';
import { PalmyraComponent } from '../dialogs/palmyra/palmyra.component';
import { CohereRPlusComponent } from '../dialogs/cohere-r-plus/cohere-r-plus.component';
import { GeminiProComponent } from '../dialogs/gemini-pro/gemini-pro.component';
import { Deepseekv3Component } from '../dialogs/deepseekv3/deepseekv3.component';
import { MistralLargeComponent } from '../dialogs/mistral-large/mistral-large.component';
import { PixtralImageComponent } from '../dialogs/pixtral-image/pixtral-image.component';
import { GptFiveComponent } from '../dialogs/gpt-five/gpt-five.component';
import { DeepseekComponent } from '../dialogs/deepseek/deepseek.component';
import { NovaProComponent } from '../dialogs/nova-pro/nova-pro.component';
import { ScoutComponent } from '../dialogs/scout/scout.component';
import { PremierComponent } from '../dialogs/premier/premier.component';
import { JambaComponent } from '../dialogs/jamba/jamba.component';
import { OpusFourComponent } from '../dialogs/opus-four/opus-four.component';
import { NotesService } from '../../services/notes.service';
import {
  CLARI_CHAT_MODEL_CATEGORIES,
  getClariChatModelNameMap,
  getClariChatModelsForTeamComparePickers,
  CLARI_CHAT_MODEL_VENDOR_BY_ID
} from 'src/app/shared/clari-chat-model-catalog';
import { UiService, OpenAiRtcSession } from '../../gen2ai/ui.service';
import { ClarityCoachService } from '../../services/clarity-coach.service';
import { ClariCoachDialogComponent } from '../dialogs/clari-coach-dialog/clari-coach-dialog.component';
import { aggressivelyFormatMarkdownForCodeBlocks } from '../../shared/markdown/aggressive-code-format';

@Component({
  selector: 'app-double-chat',
  templateUrl: './double-chat.component.html',
  styleUrls: ['./double-chat.component.scss']
})
export class DoubleChatComponent implements OnInit, AfterViewInit, OnDestroy {
  form: FormGroup;

  currentModel = 'haiku45';
  currentModel2 = 'deepv3';

  showModelAnnouncement = false;
  currentMessagesModel1: Message[] = [];
  currentMessagesModel2: Message[] = [];
  pinnedMessages1: Message[] = [];
  pinnedMessages2: Message[] = [];
  converter: showdown.Converter;

  isLoading = false;
  audioActive = false;
  isDarkMode = false;
  showScrollToBottom1 = false;
  showScrollToBottom2 = false;
  useOpenAiRtc = false;
  private openAiRtcSession: OpenAiRtcSession | null = null;
  private googleRtcSession: { close: () => void } | null = null;

  private modelBackgroundRequested = false;

  private publishCompareModelsToTopnav(): void {
    const left = this.formatModelName(this.currentModel);
    const right = this.formatModelName(this.currentModel2);

    localStorage.setItem('compareLeftModelName', left);
    localStorage.setItem('compareRightModelName', right);

    window.dispatchEvent(
      new CustomEvent('compareModelsChanged', {
        detail: { left, right }
      })
    );
  }

  private onOpenCompareModelSelector = () => this.openModelSelectionDialog();

  private onOpenCompareBios = () => {
    this.openModelDialog(this.currentModel);
    this.openModelDialog(this.currentModel2);
  };

  menuOpen = true;

  private pinnedBottom1 = false;
  private pinnedBottom2 = false;

  private ensureIds(pane: 1 | 2, msgs: Message[]): void {
    const prefix = pane === 1 ? 'm1' : 'm2';
    const now = Date.now();

    msgs.forEach((m, i) => {
      if (!m.id) {
        (m as any).id = `${prefix}-${now}-${i}`;
      }
    });
  }

  //textarea placeholder magic
  isFocused = false;
  onTextareaFocus(): void {
  this.isFocused = true;
}
  onTextareaBlur(): void {
  this.isFocused = false;
}

  openActionsMessageId: string | null = null;
  currentPinnedIndex1 = -1;
  currentPinnedIndex2 = -1;
  trackMsg = (_: number, m: Message) => m.id ?? m.timestamp;

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent): void {
    const target = ev.target as HTMLElement | null;
    if (!target) return;

    if (!target.closest('.pin-stack-anchor') && !target.closest('.pin-stack-preview')) {
      this.showPinStack1 = false;
      this.showPinStack2 = false;
    }

    if (!target.closest('.actions-anchor')) {
      this.openActionsMessageId = null;
    }
  }

  toggleActionsMenu(msg: Message, ev?: MouseEvent): void {
    ev?.stopPropagation();

    const id = msg.id || null;
    if (!id) return;

    this.openActionsMessageId = this.openActionsMessageId === id ? null : id;
  }

  isActionsMenuOpen(msg: Message): boolean {
    return !!msg.id && this.openActionsMessageId === msg.id;
  }

  onActionPin(pane: 1 | 2, msg: Message, ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.togglePinMessage(pane, msg, ev);
    this.openActionsMessageId = null;
  }

  async saveMessageToNotes(msg: Message): Promise<void> {
    if (!msg?.content?.trim()) return;

    const who = msg.role === 'user' ? 'You' : msg.modelDisplayName || 'Assistant';
    const timestamp = msg.timestamp ? new Date(msg.timestamp as any).toLocaleString() : '';
    const titleBase = `Chat – ${who}`;
    const title = timestamp ? `${titleBase} (${timestamp})` : titleBase;

    await this.notesService.createNote({
      title,
      content: msg.content,
      labels: ['ClariChat'],
      color: '#fff8e1',
      pinned: false
    });
  }

  async onActionCopy(msg: Message, i: number, ev?: MouseEvent): Promise<void> {
    void i;
    ev?.stopPropagation();

    try {
      const text = (msg.content || '').trim();
      if (text) {
        await navigator.clipboard.writeText(text);
      } else {
        const anyMsg: any = msg as any;
        const img = anyMsg.image || anyMsg.images?.[0];
        if (img) {
          const src =
            img.startsWith('http') || img.startsWith('data:')
              ? img
              : 'data:image/jpeg;base64,' + img;
          await navigator.clipboard.writeText(src);
        } else {
          await navigator.clipboard.writeText('');
        }
      }

      this.showActionToast(msg, 'Copied ✓');
    } catch {
      this.showActionToast(msg, 'Copy failed', 'warn');
    } finally {
      this.openActionsMessageId = null;
    }
  }

  async onActionCopyToClariChat(msg: Message, ev?: MouseEvent): Promise<void> {
    ev?.stopPropagation();
    try {
      await this.saveMessageToNotes(msg);
      this.showActionToast(msg, 'Added to ClariNotes ✓');
    } catch {
      this.showActionToast(msg, 'Add failed', 'warn');
    } finally {
      this.openActionsMessageId = null;
    }
  }

  actionToastById: Record<string, { text: string; type: 'copied' | 'added' | 'warn' }> = {};

  private showActionToast(
    msg: Message,
    text: string,
    type: 'copied' | 'added' | 'warn' = 'copied'
  ): void {
    const id = msg.id;
    if (!id) return;

    this.actionToastById[id] = { text, type };

    setTimeout(() => {
      if (this.actionToastById[id]?.text === text) {
        delete this.actionToastById[id];
      }
    }, 3500);
  }

  togglePinStack(pane: 1 | 2, ev?: MouseEvent): void {
    ev?.stopPropagation();
    if (pane === 1) this.showPinStack1 = !this.showPinStack1;
    else this.showPinStack2 = !this.showPinStack2;
  }

  togglePinMessage(pane: 1 | 2, msg: Message, ev?: MouseEvent): void {
    ev?.stopPropagation();
    ev?.preventDefault();

    if (!msg.id) {
      const prefix = pane === 1 ? 'm1' : 'm2';
      (msg as any).id = `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    msg.pinned = !msg.pinned;

    if (pane === 1) {
      this.pinnedMessages1 = this.currentMessagesModel1.filter(m => m.pinned);
      if (msg.pinned) this.scrollToPinnedForId(1, msg.id);
    } else {
      this.pinnedMessages2 = this.currentMessagesModel2.filter(m => m.pinned);
      if (msg.pinned) this.scrollToPinnedForId(2, msg.id);
    }
  }

  clearAllPins(pane: 1 | 2, ev?: MouseEvent): void {
    ev?.stopPropagation();

    const list = pane === 1 ? this.currentMessagesModel1 : this.currentMessagesModel2;
    list.forEach(m => (m.pinned = false));

    if (pane === 1) {
      this.pinnedMessages1 = [];
      this.showPinStack1 = false;
      this.currentPinnedIndex1 = -1;
    } else {
      this.pinnedMessages2 = [];
      this.showPinStack2 = false;
      this.currentPinnedIndex2 = -1;
    }
  }

  jumpToPinned(pane: 1 | 2, id?: string): void {
    if (!id) return;
    if (pane === 1) this.showPinStack1 = false;
    else this.showPinStack2 = false;
    this.scrollToPinnedForId(pane, id);
  }

  scrollToPinned(pane: 1 | 2, next: boolean, ev?: MouseEvent): void {
    ev?.stopPropagation();
    ev?.preventDefault();

    const pinned = pane === 1 ? this.pinnedMessages1 : this.pinnedMessages2;
    if (!pinned.length) return;

    const curr = pane === 1 ? this.currentPinnedIndex1 : this.currentPinnedIndex2;
    const nextIdx = next
      ? (curr + 1) % pinned.length
      : (curr - 1 + pinned.length) % pinned.length;

    if (pane === 1) this.currentPinnedIndex1 = nextIdx;
    else this.currentPinnedIndex2 = nextIdx;

    const target = pinned[nextIdx];
    if (target?.id) this.scrollToPinnedForId(pane, target.id);
  }

  private scrollToPinnedForId(pane: 1 | 2, id: string): void {
    const domId = `${pane === 1 ? 'm1' : 'm2'}-msg-${id}`;
    const el = document.getElementById(domId);

    if (!el) {
      console.warn('[pin] element not found:', domId);
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    el.classList.add('pinned-highlight');
    setTimeout(() => el.classList.remove('pinned-highlight'), 900);
  }

  showPinStack1 = false;
  showPinStack2 = false;

  private mo1?: MutationObserver;
  private mo2?: MutationObserver;
  private subs: Subscription[] = [];

  votedMessages: Set<string> = new Set();
  singleVotePerIndex: Set<number> = new Set();
  votes = { model1: new Set<number>(), model2: new Set<number>() };

  private modelNames: Record<string, string> = {
    ...getClariChatModelNameMap(),
    gemini3flash: 'Gemini Flash 3'
  };

  private modelVendorMap: Record<string, string> = {
    ...CLARI_CHAT_MODEL_VENDOR_BY_ID,
    palmx: 'x5-baby',
    gemini3flash: 'google'
  };

  private modelPlaceholders: Record<string, string> = {
    maverick: 'Llama 4 for general strength.',
    opus46: 'Claude Opus 4.6 — Bedrock Converse Lambda (prompt, history, optional systemPrompt).',
    opus6: 'Claude Opus 4.6 — Bedrock Converse Lambda (prompt, history, optional systemPrompt).',
    opus47: 'Claude Opus 4.7 — Bedrock Converse Lambda with catalogModelId opus47.',
    kimik25: 'Kimi 2.5 by Moonshot AI (dedicated Lambda).',
    opus37: 'Claude Opus 3.7 with balanced depth and response speed.',
    haiku45: 'Claude Haiku 4.5 stays fast and crisp.',
    sonnet45: 'Sonnet 4.5 balances speed and depth.',
    palmx: 'Palmyra X5 handles huge context windows.',
    cohere: 'Command R+ keeps enterprise replies structured.',
    commandLight: 'Command Instruct for fast, efficient enterprise chat.',
    gemini: 'Gemini Pro for balanced multimodal chat.',
    gemini3flash: 'Gemini Flash 3 answers quickly and clearly.',
    deepv3: 'DeepSeek V3.2 leans into long-context reasoning.',
    deepseek: 'DeepSeek R1 for reasoning-heavy answers.',
    mistrallarge: 'Mistral Large delivers technical depth.',
    pixtralimage: 'Pixtral excels with vision-first prompts.',
    codestralto: 'Codestral focuses on code and technical work.',
    minichatgpt54: 'ChatGPT Mini for fast, efficient replies.',
    chatgpt54: 'ChatGPT 5.4 flagship reasoning and writing.',
    chatgpt5: 'ChatGPT 5.2 line for capable general chat.',
    scout: 'Meta Scout for efficient open-model chat.',
    jamba: 'Jamba Large for long-context workflows.',
    novapro: 'Nova Pro for AWS-grade assistant quality.',
    premier: 'Nova Premier for top-tier Amazon Nova responses.',
    opus45: 'Claude Opus 4.5 for premium depth.',
    commandRInstruct: 'Command R instruct tuned for grounded answers.',
    llama31Instruct: 'Llama 3.1 instruct on Bedrock.',
    novalite: 'Nova Lite for fast Bedrock responses.'
  };

  userInput = '';
  coachFabBusy = false;
  coachFabText = 'Coach';

  /** ≤800px: single-pane UX; only the primary model receives sends. */
  mobileSinglePane = false;
  isMobilePlaceholder = false;

  get composerPlaceholderText(): string {
    return this.isMobilePlaceholder ? 'Ask me anything!' : 'Ask us anything!';
  }

  @ViewChild('messagesContainer1', { static: false })
  messagesContainer1!: ElementRef<HTMLDivElement>;

  @ViewChild('messagesContainer2', { static: false })
  messagesContainer2!: ElementRef<HTMLDivElement>;

  constructor(
    private chatService: FlameThrowerService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private notesService: NotesService,
    private route: ActivatedRoute,
    private uiService: UiService,
    private breakpointObserver: BreakpointObserver,
    private coachService: ClarityCoachService
  ) {
    this.form = this.fb.group({
      inputText: [''],
      selectedModel: ['haiku45'],
      selectedModel2: ['deepv3']
    });

    this.converter = new showdown.Converter({
      tables: true,
      simplifiedAutoLink: true,
      strikethrough: true,
      tasklists: true
    });
  }

  ngOnInit(): void {
    this.currentModel = this.form.get('selectedModel')?.value || 'haiku45';
    this.currentModel2 = this.form.get('selectedModel2')?.value || 'deepv3';
    this.publishCompareModelsToTopnav();

    const subLeft = this.form.get('selectedModel')!.valueChanges.subscribe(model => {
      this.currentModel = model;
      this.showModelAnnouncement = true;
      setTimeout(() => (this.showModelAnnouncement = false), 3000);
      this.populateLastUserMessage();
      this.publishCompareModelsToTopnav();
    });

    const subRight = this.form.get('selectedModel2')!.valueChanges.subscribe(model => {
      this.currentModel2 = model;
      this.publishCompareModelsToTopnav();
    });

    this.subs.push(subLeft, subRight);

    const subBp = this.breakpointObserver.observe('(max-width: 800px)').subscribe(state => {
      this.mobileSinglePane = state.matches;
    });
    this.subs.push(subBp);

    const subPlaceholderBp = this.breakpointObserver.observe('(max-width: 900px)').subscribe(state => {
      this.isMobilePlaceholder = state.matches;
    });
    this.subs.push(subPlaceholderBp);

    this.route.queryParams.subscribe(params => {
      if (params['openModelBackground'] && !this.modelBackgroundRequested) {
        this.modelBackgroundRequested = true;
        setTimeout(() => this.openModelDialog(this.currentModel), 0);
      }
    });

    window.addEventListener('openCompareModelBio', this.onOpenCompareModelBio as EventListener);
    window.addEventListener('openCompareModelSelector', this.onOpenCompareModelSelector as EventListener);
  }

  private onOpenCompareModelBio = (ev: Event) => {
    const side = (ev as CustomEvent).detail?.side as 'left' | 'right' | undefined;
    if (!side) return;

    if (side === 'left') {
      this.openModelDialog(this.currentModel);
    } else {
      this.openModelDialog(this.currentModel2);
    }
  };

  ngAfterViewInit(): void {
    this.setupScrollAndObserve(
      this.messagesContainer1.nativeElement,
      (v: boolean) => (this.showScrollToBottom1 = v),
      (p: boolean) => (this.pinnedBottom1 = p)
    );

    this.setupScrollAndObserve(
      this.messagesContainer2.nativeElement,
      (v: boolean) => (this.showScrollToBottom2 = v),
      (p: boolean) => (this.pinnedBottom2 = p)
    );
  }

  ngOnDestroy(): void {
    this.mo1?.disconnect();
    this.mo2?.disconnect();
    this.subs.forEach(s => s.unsubscribe());

    if (this.openAiRtcSession) {
      this.openAiRtcSession.close();
      this.openAiRtcSession = null;
    }

    if (this.googleRtcSession) {
      this.googleRtcSession.close();
      this.googleRtcSession = null;
    }

    window.removeEventListener('openCompareModelBio', this.onOpenCompareModelBio as EventListener);
    window.removeEventListener('openCompareModelSelector', this.onOpenCompareModelSelector as EventListener);
  }

  private isNearBottom(el: HTMLElement, threshold = 120): boolean {
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  }

  private safeScrollToBottom(el: HTMLElement): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  }

  private setupScrollAndObserve(
    el: HTMLElement,
    setShowBtn: (show: boolean) => void,
    setPinned: (pinned: boolean) => void
  ): void {
    setPinned(true);
    setShowBtn(false);

    el.addEventListener('scroll', () => {
      const nearBottom = this.isNearBottom(el);
      setPinned(nearBottom);
      setShowBtn(!nearBottom);
    });

    const mo = new MutationObserver(() => {
      if (this.isNearBottom(el)) {
        this.safeScrollToBottom(el);
        setShowBtn(false);
        setPinned(true);
      } else {
        setShowBtn(true);
        setPinned(false);
      }
    });

    mo.observe(el, { childList: true, subtree: true });

    if (el === this.messagesContainer1.nativeElement) this.mo1 = mo;
    if (el === this.messagesContainer2.nativeElement) this.mo2 = mo;
  }

  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    const body = document.body;
    if (this.isDarkMode) {
      body.classList.remove('light-mode');
      body.classList.add('dark-mode');
    } else {
      body.classList.remove('dark-mode');
      body.classList.add('light-mode');
    }
  }

  scrollToBottom1(): void {
    this.safeScrollToBottom(this.messagesContainer1.nativeElement);
    this.showScrollToBottom1 = false;
    this.pinnedBottom1 = true;
  }

  scrollToBottom2(): void {
    this.safeScrollToBottom(this.messagesContainer2.nativeElement);
    this.showScrollToBottom2 = false;
    this.pinnedBottom2 = true;
  }

  voteAnswer(model: 'model1' | 'model2', messageIndex: number): void {
    const voteId = `${model}-${messageIndex}`;
    if (this.singleVotePerIndex.has(messageIndex)) {
      alert('You already voted for this round!');
      return;
    }

    this.singleVotePerIndex.add(messageIndex);
    this.votedMessages.add(voteId);
    this.votes[model].add(messageIndex);
  }

  formatModelName(modelKey: string): string {
    return this.modelNames[modelKey] || modelKey.charAt(0).toUpperCase() + modelKey.slice(1);
  }

  /**
   * Label next to the message timestamp / pin meta (ChatGPT 5.4 flagship → "ChatGPT5.4").
   */
  messageTimestampModelLabel(msg: Message | any): string {
    if (!msg || msg.role !== 'assistant') {
      return '';
    }
    if (this.isChatGpt54FamilyMessage(msg)) {
      return 'ChatGPT5.4';
    }
    return String(msg.modelDisplayName || msg.modelId || '').trim();
  }

  private isChatGpt54FamilyMessage(msg: Message | any): boolean {
    const id = String(msg?.modelId || '')
      .trim()
      .toLowerCase();
    if (['chatgpt54', 'chatgpt', 'chatgpt52', 'chatgpt5', 'o3', 'chagpt54'].includes(id)) {
      return true;
    }
    const label = String(msg?.modelDisplayName || '').toLowerCase();
    return label.includes('chatgpt') && label.includes('5.4');
  }

  getVendorLogo(modelKey: string): string {
    const normalizedKey = modelKey.toLowerCase().replace(/\s+/g, '');
    const vendor = this.modelVendorMap[normalizedKey] || this.modelVendorMap[modelKey];

    if (!vendor) return 'assets/logos/iamclari2.png';
    if (vendor === 'x5-baby') return 'assets/logos/x5-baby.png';
    if (vendor === 'iamclari2') return 'assets/logos/iamclari2.png';
    if (vendor === 'scout') return 'assets/logos/scout.png';

    return `assets/logos/${vendor}.svg`;
  }

  getModelPlaceholder(modelKey: string): string {
    const normalizedKey = modelKey.toLowerCase().replace(/\s+/g, '');
    return (
      this.modelPlaceholders[normalizedKey] ||
      this.modelPlaceholders[modelKey] ||
      'Ready to assist you with your questions.'
    );
  }

  hasVendorLogo(modelKey: string): boolean {
    const normalizedKey = modelKey.toLowerCase().replace(/\s+/g, '');
    return !!(this.modelVendorMap[normalizedKey] || this.modelVendorMap[modelKey]);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private normalizeCoachText(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.replace(/\s+/g, ' ').trim();
  }

  private getLatestCoachContext(): { userText: string; assistantText: string; statusCode: number; ok: boolean } | null {
    const recent = [...(this.currentMessagesModel1 || []), ...(this.currentMessagesModel2 || [])].reverse();
    const userMessage = recent.find(message => message.role === 'user' && this.normalizeCoachText(message.content));
    const assistantMessage = recent.find(message => message.role === 'assistant' && this.normalizeCoachText(message.content));

    if (!userMessage || !assistantMessage) {
      return null;
    }

    const userText = this.normalizeCoachText(userMessage.content);
    const assistantText = this.normalizeCoachText(assistantMessage.content);
    if (!userText || !assistantText) {
      return null;
    }

    const statusCodeValue = Number(
      (assistantMessage as any).statusCode ??
      (assistantMessage as any).httpStatus ??
      (assistantMessage as any).responseStatus ??
      200
    );

    return {
      userText,
      assistantText,
      statusCode: Number.isFinite(statusCodeValue) ? statusCodeValue : 200,
      ok: true
    };
  }

  async triggerCoachFab(): Promise<void> {
    const context = this.getLatestCoachContext();
    if (!context) {
      console.warn('[DoubleChat] No successful exchange is available for AI Coach.');
      return;
    }

    if (context.statusCode !== 200) {
      console.warn('[DoubleChat] AI Coach requires a successful response status.');
      return;
    }

    this.coachFabBusy = true;
    this.coachFabText = 'Working';

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-ai-coach-menu'));
    }

    try {
      const response = await this.coachService.requestDoubleCoachDeepSeek({
        ...context,
        preference: this.coachService.getPreference()
      });
      this.coachFabText = response?.status === 'ready' ? 'Ready' : 'Coach';

      if (response?.status === 'ready') {
        this.dialog.open(ClariCoachDialogComponent, {
          width: 'min(760px, 92vw)',
          maxWidth: '92vw',
          maxHeight: '80vh',
          panelClass: 'ai-coach-dialog-panel',
          data: {
            entries: this.coachService.getRecentCoachEntries(10)
          }
        });
      }
    } catch (error) {
      this.coachFabText = 'Coach';
      console.error('[DoubleChat] AI Coach failed:', error);
    } finally {
      this.coachFabBusy = false;
    }
  }

  handleSendOrAudioClick(): void {
    if (this.userInput?.trim()) {
      this.sendMessage();
      return;
    }
    this.onAudioIconClick();
  }

  autoResize(el: HTMLTextAreaElement): void {
    const MAX = 240;

    if (!el.value || el.value.trim().length === 0) {
      el.style.height = '';
      el.style.overflowY = 'hidden';
      el.scrollTop = 0;
      return;
    }

    const cs = window.getComputedStyle(el);
    const lineH = parseFloat(cs.lineHeight || '20') || 20;

    el.style.height = '';

    const scrollH = el.scrollHeight;
    const clientH = el.clientHeight || lineH;
    const fillRatio = scrollH / clientH;
    const shouldPreGrow = fillRatio >= 0.85;
    const desired = Math.min(scrollH + (shouldPreGrow ? lineH : 0), MAX);

    el.style.height = `${desired}px`;

    const capped = desired >= MAX;
    el.style.overflowY = capped ? 'auto' : 'hidden';

    if (capped) el.scrollTop = el.scrollHeight;
  }

  getAssistantClass(model: string): string {
    return `assistant-${model}`;
  }

  openMaverickDialog(): void {
    this.dialog.open(MaverickComponent, dlgCfg());
  }

  openOpus6Dialog(): void {
    this.dialog.open(OpusSixComponent, { ...altCfg(), panelClass: 'opus-dialog-panel' });
  }

  openHaikuDialog(): void {
    this.dialog.open(HaikuComponent, altCfg());
  }

  openSonnet45Dialog(): void {
    this.dialog.open(SonnetFourComponent, altCfg());
  }

  openPalmyraDialog(): void {
    this.dialog.open(PalmyraComponent, dlgCfg());
  }

  openCohereDialog(): void {
    this.dialog.open(CohereRPlusComponent, altCfg());
  }

  openGeminiFlashDialog(): void {
    this.dialog.open(GeminiProComponent, dlgCfg());
  }

  openDeepSeekV3Dialog(): void {
    this.dialog.open(Deepseekv3Component, dlgCfg());
  }

  openMistralLargeDialog(): void {
    this.dialog.open(MistralLargeComponent, altCfg());
  }

  openPixtralDialog(): void {
    this.dialog.open(PixtralImageComponent, altCfg());
  }

  openModelSelectionDialog(): void {
    const pickerModels = getClariChatModelsForTeamComparePickers().map((m) => ({
      id: m.id,
      name: m.name
    }));

    const dialogRef = this.dialog.open(ModelSelectionDialogComponent, {
      width: '1100px',
      maxWidth: '99vw',
      panelClass: 'doublechat-model-dnd-dialog',
      data: {
        form: this.form,
        availableModels: pickerModels,
        categories: CLARI_CHAT_MODEL_CATEGORIES
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      this.currentModel = result.left;
      this.currentModel2 = result.right;

      this.form.patchValue({
        selectedModel: result.left,
        selectedModel2: result.right
      });

      this.publishCompareModelsToTopnav();
    });
  }

  openModelDialog(model: string): void {
    switch (model) {
      case 'maverick':
      case 'llama31Instruct':
        this.openMaverickDialog();
        break;
      case 'opus46':
      case 'opus6':
      case 'opus47':
        this.openOpus6Dialog();
        break;
      case 'opus37':
      case 'opus45':
        this.dialog.open(OpusFourComponent, dlgCfg());
        break;
      case 'haiku45':
        this.openHaikuDialog();
        break;
      case 'sonnet45':
        this.openSonnet45Dialog();
        break;
      case 'palmx':
        this.openPalmyraDialog();
        break;
      case 'cohere':
      case 'commandRInstruct':
      case 'commandLight':
        this.openCohereDialog();
        break;
      case 'gemini':
      case 'gemini3flash':
        this.openGeminiFlashDialog();
        break;
      case 'deepv3':
        this.openDeepSeekV3Dialog();
        break;
      case 'deepseek':
        this.dialog.open(DeepseekComponent, dlgCfg());
        break;
      case 'mistrallarge':
        this.openMistralLargeDialog();
        break;
      case 'codestralto':
        this.openMistralLargeDialog();
        break;
      case 'pixtralimage':
        this.openPixtralDialog();
        break;
      case 'chatgpt54':
      case 'chatgpt5':
      case 'minichatgpt54':
        this.dialog.open(GptFiveComponent, dlgCfg());
        break;
      case 'scout':
      case 'novalite':
        this.dialog.open(ScoutComponent, dlgCfg());
        break;
      case 'jamba':
        this.dialog.open(JambaComponent, dlgCfg());
        break;
      case 'novapro':
        this.dialog.open(NovaProComponent, dlgCfg());
        break;
      case 'premier':
        this.dialog.open(PremierComponent, dlgCfg());
        break;
    }
  }

  private populateLastUserMessage(): void {
    const lastUserMessageLeft = [...this.currentMessagesModel1].reverse().find(msg => msg.role === 'user');
    if (lastUserMessageLeft?.content) {
      this.userInput = lastUserMessageLeft.content;
    }
  }

  onAudioIconClick(): void {
    if (this.useOpenAiRtc) {
      if (this.openAiRtcSession) {
        this.openAiRtcSession.close();
        this.openAiRtcSession = null;
        this.audioActive = false;
        return;
      }

      this.audioActive = true;
      this.uiService.startOpenAiRtcSession().subscribe({
        next: session => {
          this.openAiRtcSession = session;
          if (session.remoteAudioEl && !document.body.contains(session.remoteAudioEl)) {
            session.remoteAudioEl.style.position = 'absolute';
            session.remoteAudioEl.style.width = '0';
            session.remoteAudioEl.style.height = '0';
            session.remoteAudioEl.style.opacity = '0';
            session.remoteAudioEl.style.pointerEvents = 'none';
            document.body.appendChild(session.remoteAudioEl);
          }
        },
        error: err => {
          console.error('Failed to start OpenAI RTC audio:', err);
          this.audioActive = false;
        }
      });
      return;
    }

    if (this.googleRtcSession) {
      this.googleRtcSession.close();
      this.googleRtcSession = null;
      this.audioActive = false;
      return;
    }

    this.audioActive = true;
    this.uiService
      .startGoogleAudioRtc()
      .then(session => {
        this.googleRtcSession = session;
      })
      .catch(err => {
        console.error('Failed to start Google RTC audio:', err);
        this.audioActive = false;
      });
  }

  sendMessage(): void {
    if (!this.userInput.trim() || this.isLoading) return;

    const prompt = this.userInput;
    this.userInput = '';

    const model1 = this.form.get('selectedModel')?.value || 'haiku45';
    const model2 = this.form.get('selectedModel2')?.value || 'deepv3';

    this.pinnedBottom1 = this.isNearBottom(this.messagesContainer1.nativeElement);
    this.pinnedBottom2 = this.isNearBottom(this.messagesContainer2.nativeElement);

    this.isLoading = true;

    if (this.mobileSinglePane) {
      this.chatService.sendMessage(prompt, model1).subscribe({
        next: response1 => {
          this.ensureIds(1, response1);
          this.currentMessagesModel1.push(...response1);
          this.isLoading = false;
          if (this.pinnedBottom1) this.safeScrollToBottom(this.messagesContainer1.nativeElement);
        },
        error: err => {
          console.error('Single-pane compare message error', err);
          this.isLoading = false;
        }
      });
      return;
    }

    forkJoin([this.chatService.sendMessage(prompt, model1), this.chatService.sendMessage(prompt, model2)]).subscribe({
      next: ([response1, response2]) => {
        this.ensureIds(1, response1);
        this.ensureIds(2, response2);
        this.currentMessagesModel1.push(...response1);
        this.currentMessagesModel2.push(...response2);
        this.isLoading = false;

        if (this.pinnedBottom1) this.safeScrollToBottom(this.messagesContainer1.nativeElement);
        if (this.pinnedBottom2) this.safeScrollToBottom(this.messagesContainer2.nativeElement);
      },
      error: err => {
        console.error('Dual message error', err);
        this.isLoading = false;
      }
    });
  }

  convertMarkdown(markdown: string): string {
    const aggressive = aggressivelyFormatMarkdownForCodeBlocks(markdown || '');
    return this.converter.makeHtml(aggressive);
  }
}

function dlgCfg() {
  return {
    width: '900px',
    maxWidth: '99vw',
    height: 'auto',
    maxHeight: '98vh',
    panelClass: 'deepseek-dialog-panel'
  } as const;
}

function altCfg() {
  return {
    width: '900px',
    maxWidth: '99vw',
    height: 'auto',
    maxHeight: '98vh',
    panelClass: 'Sonnet4-dialog-panel'
  } as const;
}
